import { reflect } from '@effector/reflect';
import { NumberInput, Select } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { viewerModel } from 'entities/viewer';
import { USER_ROLES } from 'shared/api';
import { $t, translated } from 'shared/lib';
import { createModalForm } from 'shared/ui';
import { AddVoucherModel } from '../../model';
import cls from './add-voucher-modal.module.scss';

export const createAddVoucherModal = (model: AddVoucherModel) => {
	const ModalForm = createModalForm(model);

	const CurrencyField = reflect({
		view: Select,
		bind: {
			'data-test-id': 'add-voucher-currency-select',
			value: model.form.fields.currency.$value,
			onChange: model.form.fields.currency.change,
			data: model.$currencies,
			label: translated('add-voucher.currency'),
			placeholder: translated('add-voucher.currency'),
			errorProps: { 'data-test-id': 'add-voucher-currency-select-error' },
			error: combine(
				[model.form.fields.currency.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-voucher.currency'),
					}),
			),
			searchable: true,
			required: true,
		},
	});

	const CountField = reflect({
		view: NumberInput,
		bind: {
			required: true,
			decimalScale: 0,
			'data-test-id': 'add-voucher-count-input',
			label: translated('add-voucher.count'),
			placeholder: translated('add-voucher.count-placeholder'),
			value: model.form.fields.count.$value,
			onChange: (value) => model.form.fields.count.change(String(value ?? '')),
			error: combine(
				[model.form.fields.count.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-voucher.count'),
					}),
			),
		},
	});

	const DepositField = reflect({
		view: NumberInput,
		bind: {
			required: true,
			decimalScale: 2,
			rightSection: model.form.fields.currency.$value,
			'data-test-id': 'add-voucher-deposit-input',
			label: translated('add-voucher.deposit'),
			placeholder: translated('add-voucher.deposit-placeholder'),
			value: model.form.fields.deposit.$value,
			onChange: (value) => model.form.fields.deposit.change(String(value ?? '')),
			error: combine(
				[model.form.fields.deposit.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-voucher.deposit'),
					}),
			),
		},
	});

	const WageringField = reflect({
		view: NumberInput,
		bind: {
			required: true,
			decimalScale: 2,
			rightSection: model.form.fields.currency.$value,
			'data-test-id': 'add-voucher-wagering-input',
			label: translated('add-voucher.wagering'),
			placeholder: translated('add-voucher.wagering-placeholder'),
			value: model.form.fields.wagering.$value,
			onChange: (value) => model.form.fields.wagering.change(String(value ?? '')),
			error: combine(
				[model.form.fields.wagering.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('add-voucher.wagering'),
					}),
			),
		},
	});

	const AddVoucherModalButton = reflect({
		view: ModalForm.Button,
		bind: {
			'data-test-id': 'add-voucher-button',
			leftSection: <Plus />,
			className: cls.addVoucherModalButton,
			disabled: viewerModel.$role.map((role) => role !== USER_ROLES.god),
			children: translated('add-voucher.button-label'),
			modalData: null,
		},
	});

	const AddVoucherModal = () => {
		const { t } = useTranslation();

		const pending = useUnit(model.$pending);

		return (
			<ModalForm
				title={t('add-voucher.title')}
				cancelButtonProps={{
					children: t('add-voucher.cancel'),
				}}
				submitButtonProps={{
					children: t('add-voucher.add'),
					loading: pending,
				}}
			>
				<CurrencyField />
				<CountField />
				<DepositField />
				<WageringField />
			</ModalForm>
		);
	};

	return Object.assign(AddVoucherModal, {
		Button: AddVoucherModalButton,
	});
};
